import {
  fetchAnalysisSectionDataVersion2,
  filterEndDate,
  filterStartDate,
  getAppliedFilters,
  getBlockDimensions,
  getBlockDetails,
  getDimension,
  overallGroupByFilter,
  overallIndicatorFilter,
  overallTimeFilter,
  setPlanPageFetchGeneration,
} from 'redux/PlanPageSlice';
import { GetInputAdjustments } from 'redux/driverAdjustmentSlice';
import {
  fetchCompareAnalysisSectionData,
  fetchCompareTableData,
  getBaseScenario,
  getCompareScenario,
} from 'redux/ScenarioSlice';
import { AppDispatch } from 'utils/GlobalHelpers';

import { persistActiveDimFilterSubsets, readSavedDimFilterForApi } from './planPageFilterHydration';

interface BlockDimensionsLike {
  dimensions?: Array<{ name: string; items?: string[] }>;
}

interface BuildFetchParametersOptions {
  blockId: string | undefined;
  modelId?: string | number;
  baseScenarioId: number | undefined;
  selectedGroupBy: string | number | null;
  dimension: { id?: number } | null;
  dateFilterSelected: boolean;
  dateFilter: string;
  selectedYear: string | null;
  appliedFilters: unknown;
  blockDimensions?: BlockDimensionsLike | null;
  inputAdjustments: Record<string, unknown> | null;
  indicatorFilter: string;
  compareScenarioId?: number;
  fetchGeneration?: number;
}

/**
 * Returns only dimensions with a strict subset selected for the API dim_filter param.
 * Full "select all" dimensions are omitted so the server applies the active filter only.
 */
export const resolveDimFilter = (
  appliedFilters: unknown,
  blockDimensions?: BlockDimensionsLike | null,
) => {
  if (!Array.isArray(appliedFilters)) return [];

  if (!blockDimensions?.dimensions?.length) {
    return appliedFilters;
  }

  return appliedFilters.filter((dim) => {
    const [name, selected] = Object.entries(dim ?? {})[0] ?? [];
    if (!name || !Array.isArray(selected) || selected.length === 0) return false;

    const meta = blockDimensions.dimensions?.find((dimension) => dimension.name === name);
    const totalItems = meta?.items?.length ?? 0;
    return totalItems > 0 && selected.length < totalItems;
  });
};

/**
 * Builds dim_filter entries for v2. Persisted localStorage subsets are the source of
 * truth because Redux is often reset to "all selected" when block dimension metadata loads.
 */
export const resolveDimFilterForApi = (
  filters: unknown,
  blockDimensions: BlockDimensionsLike | null | undefined,
  modelId?: string | number,
  blockId?: string | number,
): unknown[] => {
  const dimensionMeta = blockDimensions?.dimensions?.map((dimension) => ({
    name: dimension.name,
    items: dimension.items ?? [],
  }));

  const savedSubsets = persistActiveDimFilterSubsets(
    readSavedDimFilterForApi(modelId, blockId),
    dimensionMeta,
  );
  if (savedSubsets.length > 0) {
    return savedSubsets;
  }

  if (dimensionMeta?.length) {
    const reduxSubsets = resolveDimFilter(filters, blockDimensions);
    if (reduxSubsets.length > 0) {
      return reduxSubsets;
    }
  }

  if (Array.isArray(filters)) {
    return persistActiveDimFilterSubsets(filters as Record<string, string[]>[], dimensionMeta);
  }

  return [];
};

export { serializeDimFilter } from 'utils/GlobalHelpers';

/** Matches localStorage keys used when persisting filters (blockDetails.model_id). */
export const resolvePlanPageStorageModelId = (
  state: unknown,
  routeModelId?: string | number,
): string | number | undefined => {
  const blockDetail = getBlockDetails(state) as { model_id?: string | number } | null;
  return blockDetail?.model_id ?? routeModelId;
};

/**
 * Resolves the dimension filter payload for v2 requests from applied Filters only.
 * Draft checkbox edits live in selectedBlockDimension until Apply (handleFilters).
 */
export const resolvePlanPageFiltersForFetch = (state: unknown) => {
  return getAppliedFilters(state) ?? [];
};

export const buildTimeFilter = (
  dateFilterSelected: boolean,
  dateFilter: string,
  selectedYear: string | null,
) => {
  if (dateFilterSelected) return dateFilter;
  if (!selectedYear || selectedYear === 'all') return null;
  return selectedYear;
};

export const buildDimId = (
  selectedGroupBy: string | number | null,
  dimension: { id?: number } | null,
) => {
  if (selectedGroupBy === 'Total') return null;
  if (dimension && !selectedGroupBy) return dimension?.id ?? null;
  if (selectedGroupBy) return Number.parseInt(String(selectedGroupBy), 10);
  return null;
};

export const buildAnalysisParameters = ({
  blockId,
  modelId,
  baseScenarioId,
  selectedGroupBy,
  dimension,
  dateFilterSelected,
  dateFilter,
  selectedYear,
  appliedFilters,
  blockDimensions,
  inputAdjustments,
  indicatorFilter,
  compareScenarioId,
}: BuildFetchParametersOptions) => {
  const baseParameters = {
    dim_id: buildDimId(selectedGroupBy, dimension),
    dim_sort: 'ASC' as const,
    time_filter: buildTimeFilter(dateFilterSelected, dateFilter, selectedYear),
    time_aggregation: 'Y,Q,M',
    scenario_id: baseScenarioId,
    dim_filter: JSON.stringify(
      resolveDimFilterForApi(appliedFilters, blockDimensions, modelId, blockId),
    ),
    ...(inputAdjustments !== null &&
      Object.keys(inputAdjustments).length > 0 && {
        input_adjustments: inputAdjustments,
      }),
  };

  if (compareScenarioId) {
    return {
      ...baseParameters,
      indicator_filter: indicatorFilter,
      compare_scenario_id: compareScenarioId,
    };
  }

  return baseParameters;
};

export const buildCompareTableParameters = (options: BuildFetchParametersOptions) => {
  const analysisParameters = buildAnalysisParameters(options);
  const { baseScenarioId, compareScenarioId, indicatorFilter, inputAdjustments } = options;

  return {
    dim_id: analysisParameters.dim_id,
    dim_sort: analysisParameters.dim_sort,
    time_aggregation: analysisParameters.time_aggregation,
    indicator_filter: indicatorFilter,
    scenario_id: baseScenarioId,
    compare_scenario_id: compareScenarioId,
    dim_filter: analysisParameters.dim_filter,
    ...(inputAdjustments !== null &&
      Object.keys(inputAdjustments).length > 0 && {
        input_adjustments: inputAdjustments,
      }),
  };
};

export const fetchPlanPageAnalysisData = (
  dispatch: AppDispatch,
  options: BuildFetchParametersOptions,
) => {
  const { blockId, compareScenarioId, baseScenarioId, fetchGeneration } = options;
  if (!blockId || !baseScenarioId) return;

  if (compareScenarioId) {
    const chartParameters = buildAnalysisParameters(options);
    const tableParameters = buildCompareTableParameters(options);

    dispatch(
      fetchCompareAnalysisSectionData({
        blockId: Number(blockId),
        parameters: { params: chartParameters },
      }),
    );
    dispatch(
      fetchCompareTableData({
        blockId: Number(blockId),
        parameters: { params: tableParameters },
      }),
    );
    return;
  }

  dispatch(
    fetchAnalysisSectionDataVersion2({
      blockId: Number(blockId),
      fetchGeneration,
      parameters: buildAnalysisParameters(options),
    }),
  );
};

export const refetchPlanPageFromStore = (
  dispatch: AppDispatch,
  getState: () => unknown,
  blockId: string | undefined,
  modelId?: string | number,
) => {
  const state = getState();
  const baseScenario = getBaseScenario(state);
  if (!blockId || !baseScenario?.id) return;

  const startDate = filterStartDate(state);
  const endDate = filterEndDate(state);
  const dateFilterSelected = Boolean(startDate && endDate);

  const compareScenario = getCompareScenario(state);
  const compareScenarioId = compareScenario?.id;
  const planPageState = (state as { planPageSlice?: { planPageFetchGeneration?: number } })
    .planPageSlice;
  const fetchGeneration = (planPageState?.planPageFetchGeneration ?? 0) + 1;

  dispatch(setPlanPageFetchGeneration(fetchGeneration));

  const storageModelId = resolvePlanPageStorageModelId(state, modelId);

  fetchPlanPageAnalysisData(dispatch, {
    blockId,
    modelId: storageModelId,
    baseScenarioId: baseScenario.id,
    selectedGroupBy: overallGroupByFilter(state),
    dimension: getDimension(state),
    dateFilterSelected,
    dateFilter: `[{ 'start_date': '${startDate}', 'end_date': '${endDate}' }]`,
    selectedYear: overallTimeFilter(state),
    appliedFilters: resolvePlanPageFiltersForFetch(state),
    blockDimensions: getBlockDimensions(state),
    inputAdjustments: GetInputAdjustments(state),
    indicatorFilter: overallIndicatorFilter(state),
    compareScenarioId,
    fetchGeneration,
  });
};

/** Builds a stable signature of the v2 request params to dedupe identical refetches. */
export const buildPlanPageFetchSignature = (
  state: unknown,
  blockId: string | undefined,
  modelId?: string | number,
): string | null => {
  const baseScenario = getBaseScenario(state);
  if (!blockId || !baseScenario?.id) return null;

  const startDate = filterStartDate(state);
  const endDate = filterEndDate(state);
  const dateFilterSelected = Boolean(startDate && endDate);
  const compareScenario = getCompareScenario(state);

  return JSON.stringify(
    buildAnalysisParameters({
      blockId,
      modelId: resolvePlanPageStorageModelId(state, modelId),
      baseScenarioId: baseScenario.id,
      selectedGroupBy: overallGroupByFilter(state),
      dimension: getDimension(state),
      dateFilterSelected,
      dateFilter: `[{ 'start_date': '${startDate}', 'end_date': '${endDate}' }]`,
      selectedYear: overallTimeFilter(state),
      appliedFilters: resolvePlanPageFiltersForFetch(state),
      blockDimensions: getBlockDimensions(state),
      inputAdjustments: GetInputAdjustments(state),
      indicatorFilter: overallIndicatorFilter(state),
      compareScenarioId: compareScenario?.id,
    }),
  );
};

type PlanPageRefetchSchedulerState = {
  timerId: ReturnType<typeof setTimeout> | null;
  lastSignature: string | null;
  /** True while a force refetch is queued (input-panel close); blocks debounced overwrites. */
  pendingForce: boolean;
};

const planPageRefetchScheduler: PlanPageRefetchSchedulerState = {
  timerId: null,
  lastSignature: null,
  pendingForce: false,
};

/** Clears any pending debounced refetch and signature cache (e.g. on block navigation). */
export const resetPlanPageRefetchScheduler = () => {
  if (planPageRefetchScheduler.timerId) {
    clearTimeout(planPageRefetchScheduler.timerId);
    planPageRefetchScheduler.timerId = null;
  }
  planPageRefetchScheduler.lastSignature = null;
  planPageRefetchScheduler.pendingForce = false;
};

export interface PlanPageRefetchOptions {
  /** When true, bypass signature dedupe (e.g. after input-panel cell edits). */
  force?: boolean;
}

/** Callback shape for plan-page v2 refetches from filters, inputs, and toolbar actions. */
export type PlanPageRefetchRequest = (options?: PlanPageRefetchOptions) => void;

/** Driver types whose input panel always covers the main plan chart and table. */
export const isAlwaysFullscreenInputDriver = (
  driver: { type?: string } | null | undefined,
): boolean => driver?.type === 'dimension' || driver?.type === 'property';

/**
 * Whether an in-panel cell edit should trigger plan-page /v2.
 * Skips when plan analytics are hidden (fullscreen indicator) or the driver is always fullscreen.
 * Close/switch handlers use {@link refetchPlanPageAfterInputPanelClose} instead.
 */
export const shouldRefetchPlanPageV2OnInputEdit = (
  showPlanPage: boolean,
  driver?: { type?: string } | null | undefined,
): boolean => {
  if (isAlwaysFullscreenInputDriver(driver)) return false;
  return showPlanPage;
};

/**
 * Refetch wrapper for input-panel saves: /v2 only when plan chart/table are visible.
 * Fullscreen indicator edits should refresh the input chart via /details only (handled in the panel).
 */
export const createInputPanelEditRefetch =
  (
    refetchRequest: PlanPageRefetchRequest,
    showPlanPage: boolean,
    driver?: { type?: string } | null | undefined,
  ): PlanPageRefetchRequest =>
  (options) => {
    // Close/switch handlers pass force: true — always refetch /v2 when leaving the panel.
    if (options?.force) {
      refetchRequest({ ...options, force: true });
      return;
    }
    if (!shouldRefetchPlanPageV2OnInputEdit(showPlanPage, driver)) return;
    refetchRequest({ ...options, force: true });
  };

/** Ensures input-panel saves bypass v2 signature dedupe when /v2 is requested. */
export const withForcedPlanPageRefetch =
  (refetchRequest: PlanPageRefetchRequest): PlanPageRefetchRequest =>
  (options) =>
    refetchRequest({ ...options, force: true });

/** Force plan-page v2 recalc when leaving an open input driver (close or switch). */
export const refetchPlanPageAfterInputPanelClose = (
  refetchRequest: PlanPageRefetchRequest,
  selectedDriver: { type?: string } | null | undefined,
) => {
  if (
    selectedDriver?.type === 'indicator' ||
    selectedDriver?.type === 'dimension' ||
    selectedDriver?.type === 'property'
  ) {
    refetchRequest({ force: true });
  }
};

interface SchedulePlanPageRefetchOptions extends PlanPageRefetchOptions {
  delayMs?: number;
}

/**
 * Debounces plan-page v2 fetches so rapid filter/group-by/hydration updates
 * coalesce into a single backend call with the final parameter set.
 */
export const schedulePlanPageRefetch = (
  dispatch: AppDispatch,
  getState: () => unknown,
  blockId: string | undefined,
  options: SchedulePlanPageRefetchOptions = {},
  modelId?: string | number,
) => {
  const { delayMs = 150, force = false } = options;
  const resolvedDelayMs = force ? 0 : delayMs;

  // Input-panel close schedules force:true; setDriver(null) then flips showPlanPage and triggers
  // a debounced filter effect — that must not cancel the pending force refetch.
  if (planPageRefetchScheduler.timerId && planPageRefetchScheduler.pendingForce && !force) {
    return;
  }

  if (planPageRefetchScheduler.timerId) {
    clearTimeout(planPageRefetchScheduler.timerId);
  }

  planPageRefetchScheduler.pendingForce = force;

  planPageRefetchScheduler.timerId = setTimeout(() => {
    planPageRefetchScheduler.timerId = null;
    const shouldForce = planPageRefetchScheduler.pendingForce;
    planPageRefetchScheduler.pendingForce = false;
    const signature = buildPlanPageFetchSignature(getState(), blockId, modelId);
    if (!signature) return;
    if (!shouldForce && signature === planPageRefetchScheduler.lastSignature) return;

    planPageRefetchScheduler.lastSignature = signature;
    refetchPlanPageFromStore(dispatch, getState, blockId, modelId);
  }, resolvedDelayMs);
};
